import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer } from 'lucide-react';
import {
  FREIGHT_MODE_LABELS,
  PAYMENT_METHOD_LABELS,
  PURCHASE_ORDER_STATUS_LABELS,
  formatCents,
  formatQty,
  type Company,
  type PurchaseOrder,
} from '@amai/contracts';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';
import { formatDate, formatDateTime, formatDocument, formatPhone } from '@/lib/format';
import { Button } from '@/components/Button';

/**
 * O pedido em folha, para mandar ao fornecedor.
 *
 * Não gera PDF no servidor de propósito: o próprio navegador imprime em papel
 * ou salva em PDF pelo diálogo de impressão, e o operador manda pelo WhatsApp.
 * Uma biblioteca de PDF no back seria uma dependência a mais para produzir
 * exatamente a mesma folha.
 *
 * Fica FORA do shell autenticado — sem menu lateral, sem cabeçalho. O que sai
 * na impressora é só o documento.
 */
export function PedidoImpressaoPage({ id }: { id: string }) {
  const orderQuery = useQuery({
    queryKey: ['purchase-order', id],
    queryFn: () => api.get<PurchaseOrder>(`/purchase-orders/${id}`),
  });
  const companyQuery = useQuery({
    queryKey: ['company'],
    queryFn: () => api.get<Company>('/company'),
  });

  const order = orderQuery.data;
  const company = companyQuery.data;

  if (orderQuery.isPending || companyQuery.isPending) {
    return <p className="folha__aviso">Carregando o pedido…</p>;
  }
  if (orderQuery.isError || !order) {
    return <p className="folha__aviso">{errorMessage(orderQuery.error)}</p>;
  }

  const endereco = company
    ? [
        [company.street, company.number].filter(Boolean).join(', '),
        company.district,
        [company.city, company.uf].filter(Boolean).join('/'),
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <div className="folha__fundo">
      <div className="folha__barra">
        <Button variant="outline" icon={ArrowLeft} onClick={() => window.close()}>
          Fechar
        </Button>
        <Button variant="primary" icon={Printer} onClick={() => window.print()}>
          Imprimir ou salvar em PDF
        </Button>
      </div>

      <article className="folha">
        <header className="folha__cabecalho">
          <div className="folha__identidade">
            <img className="folha__logo" src="/logo-amai.png" alt="" aria-hidden />
            <div>
            <div className="folha__empresa">{company?.name ?? 'AMAI Park'}</div>
            {company?.document && (
              <div className="folha__linha">CNPJ {formatDocument(company.document)}</div>
            )}
            {endereco && <div className="folha__linha">{endereco}</div>}
            <div className="folha__linha">
              {company?.phone ? formatPhone(company.phone) : ''}
              {company?.phone && company?.email ? ' · ' : ''}
              {company?.email ?? ''}
            </div>
            </div>
          </div>
          <div className="folha__titulo">
            <div className="folha__doc">Pedido de compra</div>
            <div className="folha__codigo">
              {order.code}
              {order.revision > 0 && ` · revisão ${order.revision}`}
            </div>
            <div className="folha__linha">
              Emitido em {formatDate(order.issuedAt ?? order.createdAt)}
            </div>
            <div className="folha__linha">{PURCHASE_ORDER_STATUS_LABELS[order.status]}</div>
          </div>
        </header>

        {order.status === 'CANCELADO' && (
          <p className="folha__cancelado">
            PEDIDO CANCELADO{order.cancelReason ? ` — ${order.cancelReason}` : ''}
          </p>
        )}

        <section className="folha__blocos">
          <div className="folha__bloco">
            <h2 className="folha__bloco-titulo">Fornecedor</h2>
            <div className="folha__forte">{order.supplierLegalName}</div>
            {order.supplierName !== order.supplierLegalName && (
              <div className="folha__linha">{order.supplierName}</div>
            )}
            {order.supplierDocument && (
              <div className="folha__linha">CNPJ/CPF {formatDocument(order.supplierDocument)}</div>
            )}
            {order.supplierPhone && (
              <div className="folha__linha">{formatPhone(order.supplierPhone)}</div>
            )}
            {order.supplierEmail && <div className="folha__linha">{order.supplierEmail}</div>}
          </div>

          <div className="folha__bloco">
            <h2 className="folha__bloco-titulo">Entrega e pagamento</h2>
            <Par rotulo="Entregar em" valor={order.locationName ?? (endereco || '—')} />
            {order.deliveryAddress && <Par rotulo="Endereço" valor={order.deliveryAddress} />}
            <Par
              rotulo="Data prevista"
              valor={order.expectedAt ? formatDate(order.expectedAt) : 'A combinar'}
            />
            <Par
              rotulo="Prazo"
              valor={order.deliveryDays === null ? '—' : `${order.deliveryDays} dias`}
            />
            <Par
              rotulo="Forma de pagamento"
              valor={order.paymentMethod ? PAYMENT_METHOD_LABELS[order.paymentMethod] : 'A combinar'}
            />
            <Par rotulo="Condição" valor={order.paymentTerms ?? '—'} />
            {order.freightMode && (
              <Par rotulo="Frete" valor={FREIGHT_MODE_LABELS[order.freightMode]} />
            )}
            {order.carrierName && <Par rotulo="Transportadora" valor={order.carrierName} />}
          </div>
        </section>

        <table className="folha__tabela">
          <thead>
            <tr>
              <th className="folha__col-num">#</th>
              <th>Descrição</th>
              <th className="folha__col-cod">Código</th>
              <th className="folha__col-un">Un.</th>
              <th className="folha__col-num">Qtd.</th>
              <th className="folha__col-valor">Preço unit.</th>
              <th className="folha__col-valor">Total</th>
            </tr>
          </thead>
          <tbody>
            {order.items.map((item, index) => (
              <tr key={item.id}>
                <td className="folha__col-num">{index + 1}</td>
                <td>
                  {item.description}
                  {(item.specification || item.brand || item.model) && (
                    <div className="folha__linha">
                      {[item.specification, item.brand, item.model].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td className="folha__col-cod">{item.sku ?? '—'}</td>
                <td className="folha__col-un">{item.unit}</td>
                <td className="folha__col-num">{formatQty(item.quantityMilli)}</td>
                <td className="folha__col-valor">{formatCents(item.unitPriceCents)}</td>
                <td className="folha__col-valor">{formatCents(item.totalCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="folha__totais">
          <Total rotulo="Itens" valor={formatCents(order.itemsCents)} />
          {order.freightCents > 0 && <Total rotulo="Frete" valor={formatCents(order.freightCents)} />}
          {order.discountCents > 0 && (
            <Total rotulo="Desconto" valor={`− ${formatCents(order.discountCents)}`} />
          )}
          <Total forte rotulo="Total do pedido" valor={formatCents(order.totalCents)} />
        </section>

        {order.supplierInstructions && (
          <section className="folha__obs">
            <h2 className="folha__bloco-titulo">Instruções</h2>
            <p>{order.supplierInstructions}</p>
          </section>
        )}

        <section className="folha__assinaturas">
          <div className="folha__assinatura">
            <span />
            {company?.tradeName ?? company?.name ?? 'Comprador'}
          </div>
          <div className="folha__assinatura">
            <span />
            {order.supplierLegalName}
          </div>
        </section>

        <footer className="folha__rodape">
          {order.costCenterName && <>Centro de custo: {order.costCenterName} · </>}
          {order.requisitionCode && <>Requisição {order.requisitionCode} · </>}
          {order.quotationCode && <>Cotação {order.quotationCode} · </>}
          Emitido pelo AMAI OPS em {formatDateTime(new Date())}
        </footer>
      </article>
    </div>
  );
}

function Par({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="folha__par">
      <span>{rotulo}</span>
      <strong>{valor}</strong>
    </div>
  );
}

function Total({ rotulo, valor, forte }: { rotulo: string; valor: string; forte?: boolean }) {
  return (
    <div className={forte ? 'folha__total folha__total--forte' : 'folha__total'}>
      <span>{rotulo}</span>
      <strong>{valor}</strong>
    </div>
  );
}
